import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { inr, pct, num, fmtDate, profitColor } from "../lib/format";

const LTP_CACHE_KEY = "portfolio-ltp-cache-v1";

const Tile = ({ label, value, sub, mono = true, accent, testid }) => (
  <Card className="border-zinc-200 shadow-none" data-testid={testid}>
    <CardContent className="p-5">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{label}</div>
      <div className={`mt-2 ${mono ? "font-mono" : "font-display"} text-2xl font-medium tabular-nums ${accent || "text-zinc-950"}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </CardContent>
  </Card>
);

// Days between an ISO date string and today (rounded down, never negative).
function holdingDays(buyDateIso) {
  if (!buyDateIso) return 0;
  const d = new Date(buyDateIso);
  if (Number.isNaN(d.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24)));
}

const SECTION_HEADER = "px-6 py-4 border-b border-zinc-200 flex items-center justify-between";
const SECTION_TITLE = "font-display font-semibold text-zinc-950";
const DISCLAIMER = "Prices sourced from Yahoo Finance · Delayed ~15 min during market hours · Verify on NSE before transacting";

// -----------------------------------------------------------------------------
// Local persistent LTP cache: survives page reload so we never show blank
// -----------------------------------------------------------------------------
const loadLtpCache = () => {
  try { return JSON.parse(localStorage.getItem(LTP_CACHE_KEY) || "{}"); } catch { return {}; }
};
const saveLtpCache = (map) => {
  try { localStorage.setItem(LTP_CACHE_KEY, JSON.stringify(map)); } catch { /* ignore */ }
};

// -----------------------------------------------------------------------------
// Monthly returns computation over a merged series [{date, portfolio, nifty50, smallcap50}]
// -----------------------------------------------------------------------------
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function computeMonthlyReturns(series, field) {
  // group by YYYY-MM, take first + last non-null value in each month
  const byMonth = {};
  series.forEach((row) => {
    const val = row[field];
    if (val == null) return;
    const ym = row.date.slice(0, 7); // YYYY-MM
    if (!byMonth[ym]) byMonth[ym] = { first: val, last: val, firstDate: row.date, lastDate: row.date };
    else { byMonth[ym].last = val; byMonth[ym].lastDate = row.date; }
  });
  const out = {};
  Object.entries(byMonth).forEach(([ym, v]) => {
    const [y, m] = ym.split("-");
    if (!out[y]) out[y] = {};
    out[y][parseInt(m, 10) - 1] = v.first ? ((v.last - v.first) / v.first) * 100 : null;
  });
  return out; // { "2026": { 0: pct, 1: pct, ... } }
}

function cellClass(v) {
  if (v == null) return "text-zinc-400";
  if (v > 3) return "text-emerald-800 bg-emerald-100 font-semibold";
  if (v > 0) return "text-emerald-700";
  if (v === 0) return "text-zinc-500";
  if (v > -3) return "text-rose-600";
  return "text-rose-800 bg-rose-100 font-semibold";
}

function monthDisplay(v, isCurrent) {
  if (v == null) return <span className="text-zinc-400">—</span>;
  const sign = v > 0 ? "+" : "";
  return <span>{sign}{v.toFixed(2)}%{isCurrent ? "*" : ""}</span>;
}

// -----------------------------------------------------------------------------
// Industry palette (with Cash)
// -----------------------------------------------------------------------------
const INDUSTRY_COLORS = {
  Cash: "#888780",
  "Cash & Equivalents": "#888780",
  IT: "#2a78d6",
  "Information Technology": "#2a78d6",
  "Industrial Products": "#1baf7a",
  Industrial: "#1baf7a",
  "Consumer Durables": "#eda100",
  Consumer: "#eda100",
  Pharma: "#e34948",
  Pharmaceuticals: "#e34948",
  Construction: "#4a3aa7",
  Textiles: "#eb6834",
};
const FALLBACK_PALETTE = ["#0f766e", "#7c3aed", "#db2777", "#ea580c", "#0369a1", "#65a30d", "#a16207", "#9333ea"];
const colorFor = (industry, idx) => INDUSTRY_COLORS[industry] || FALLBACK_PALETTE[idx % FALLBACK_PALETTE.length];

export default function PortfolioOverview({ data }) {
  if (!data) return null;
  const { kpis, performance, active_holdings, exited_holdings, chart_series, industry_distribution } = data;
  return (
    <PortfolioBody
      kpis={kpis}
      performance={performance}
      active_holdings={active_holdings || []}
      exited_holdings={exited_holdings || []}
      chart_series={chart_series || []}
      industry_distribution={industry_distribution || []}
      strategyMaxStocks={data.strategy_max_stocks || 50}
    />
  );
}

function PortfolioBody({ kpis, performance, active_holdings, exited_holdings, chart_series, industry_distribution, strategyMaxStocks }) {
  const [livePrices, setLivePrices] = useState(() => loadLtpCache());
  const [marketState, setMarketState] = useState(null);

  useEffect(() => {
    const syms = active_holdings.map((h) => h.symbol).filter(Boolean);
    if (!syms.length) return;
    const yahooSyms = syms.map((s) => (s.includes(".") || s.startsWith("^") ? s : `${s}.NS`));
    let cancelled = false;
    const fetchLtps = async () => {
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stock-prices?symbols=${encodeURIComponent(yahooSyms.join(","))}`,
        );
        const d = await r.json();
        if (cancelled) return;
        setLivePrices((prev) => {
          const map = { ...prev };
          let firstMarketState = null;
          (d.quotes || []).forEach((q) => {
            const base = (q.symbol || "").replace(/\.(NS|BO)$/, "");
            if (q.price != null) {
              map[base] = { ...q, cached_at: Date.now() };
              if (!firstMarketState) firstMarketState = q.market_state;
            }
          });
          if (firstMarketState) setMarketState(firstMarketState);
          saveLtpCache(map);
          return map;
        });
      } catch { /* keep cached */ }
    };
    fetchLtps();
    const t = setInterval(fetchLtps, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
    // active_holdings is a fresh array on every render; using its length keeps the
    // fetch stable while still re-triggering when holdings actually change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active_holdings.length]);

  const ltpFor = useCallback(
    (sym) => livePrices[(sym || "").replace(/\.(NS|BO)$/, "")],
    [livePrices],
  );

  // Position-level P&L totals used both for the Active Holdings totals row AND
  // to derive Absolute Profit / Absolute Returns / CAGR displayed on the KPI cards.
  // Backend can drift from these totals when Yahoo LTPs on the frontend differ from
  // the server-side `h.live_ltp` snapshot; sourcing all three KPIs from these totals
  // guarantees an exact match with the P&L columns below.
  const totals = useMemo(() => {
    let totBuy = 0, totCur = 0, hasAnyLtp = false;
    active_holdings.forEach((h) => {
      const q = ltpFor(h.symbol);
      const { ltp } = resolveLtp(q, h);
      totBuy += h.buy_price * h.qty;
      if (ltp != null) {
        totCur += ltp * h.qty;
        hasAnyLtp = true;
      } else {
        totCur += h.buy_price * h.qty;
      }
    });
    return {
      totBuy, totCur,
      totPnl: hasAnyLtp ? totCur - totBuy : null,
      totPnlPct: hasAnyLtp && totBuy ? ((totCur - totBuy) / totBuy) * 100 : null,
      hasAnyLtp,
    };
  }, [active_holdings, ltpFor]);

  const exitedTotals = useMemo(() => {
    let pnl = 0;
    (exited_holdings || []).forEach((h) => {
      if (h.pnl != null) pnl += h.pnl;
      else pnl += ((h.sell_price || 0) - (h.buy_price || 0)) * (h.qty || 0);
    });
    return { totPnl: pnl };
  }, [exited_holdings]);

  // Derived KPI values that always match the on-screen P&L columns.
  const derivedKpis = useMemo(() => {
    const activePnl = totals.totPnl != null ? totals.totPnl : 0;
    const absProfit = activePnl + exitedTotals.totPnl;
    const initial = kpis.initial_value || 0;
    const absReturnsPct = initial ? (absProfit / initial) * 100 : null;
    // Recompute CAGR using the corrected current_value + the KPI's already-computed
    // years-since-start (embedded in kpis.cagr_pct's original math via start_date).
    let cagrPct = kpis.cagr_pct;
    if (kpis.start_date && initial && absProfit != null) {
      const startMs = new Date(kpis.start_date).getTime();
      const years = (Date.now() - startMs) / (1000 * 60 * 60 * 24 * 365.25);
      if (years >= (30 / 365.25)) {
        const ratio = (initial + absProfit) / initial;
        cagrPct = (Math.pow(Math.max(ratio, 0.0001), 1 / years) - 1) * 100;
      } else {
        cagrPct = null;
      }
    }
    return {
      ...kpis,
      current_value: initial + absProfit,
      absolute_profit: absProfit,
      absolute_returns_pct: absReturnsPct,
      cagr_pct: cagrPct,
    };
  }, [totals.totPnl, exitedTotals.totPnl, kpis]);

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-6" data-testid="portfolio-overview">
        <KpiGrid kpis={derivedKpis} performance={performance} active_holdings={active_holdings} />

        <PerformanceChart series={chart_series} kpis={derivedKpis} />

        <IndustryPieChart industry_distribution={industry_distribution} cash={kpis.cash_balance || 0} total={derivedKpis.current_value || kpis.initial_value || 0} />

        <CashVsStocksChart series={chart_series} maxStocks={strategyMaxStocks} />

        <MonthlyReturnsTable series={chart_series} />

        <ActiveHoldingsTable
          active_holdings={active_holdings}
          ltpFor={ltpFor}
          marketState={marketState}
          totals={totals}
        />

        <ExitedHoldingsTable exited_holdings={exited_holdings} />

        <div className="text-xs text-zinc-500">
          Cash balance: <span className="font-mono text-zinc-900">{inr(kpis.cash_balance || 0)}</span>
        </div>
      </div>
    </TooltipProvider>
  );
}

// -----------------------------------------------------------------------------
// KPI grid (unchanged from prior minimal version)
// -----------------------------------------------------------------------------
function KpiGrid({ kpis, performance, active_holdings }) {
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile testid="kpi-current-value" label="Current Value" value={inr(kpis.current_value)} sub={`Start: ${fmtDate(kpis.start_date)}`} />
        <Tile testid="kpi-initial-value" label="Initial Value" value={inr(kpis.initial_value)} />
        <Tile testid="kpi-abs-profit" label="Absolute Profit" value={inr(kpis.absolute_profit)} accent={profitColor(kpis.absolute_profit)} />
        <Tile testid="kpi-cagr" label="CAGR" value={pct(kpis.cagr_pct)} accent={profitColor(kpis.cagr_pct)} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile testid="kpi-abs-returns" label="Absolute Returns" value={pct(kpis.absolute_returns_pct)} accent={profitColor(kpis.absolute_returns_pct)} />
        <Tile testid="kpi-nifty50" label="Nifty 50 (Abs / CAGR)" value={`${pct(kpis.nifty50_abs_pct)} / ${pct(kpis.nifty50_cagr_pct)}`} mono={false} />
        <Tile testid="kpi-smallcap" label="Smallcap 50 (Abs / CAGR)" value={`${pct(kpis.smallcap50_abs_pct)} / ${pct(kpis.smallcap50_cagr_pct)}`} mono={false} />
        <Tile testid="kpi-mdd" label="Max Drawdown" value={pct(-kpis.max_drawdown_pct, { showSign: false })} accent="text-loss" sub={`Recovery: ${kpis.mdd_recovery_days ?? "—"} days`} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile testid="kpi-calmar" label="Calmar Ratio" value={num(kpis.calmar_ratio, 2)} />
        <Tile testid="kpi-rtmdd" label="Return / MDD" value={num(kpis.return_to_mdd, 2)} />
        <Tile testid="kpi-trades" label="Total Trades" value={num(performance.total_trades, 0)} sub={performance.closed_trades != null ? `${performance.closed_trades} closed` : undefined} />
        <Tile testid="kpi-winrate" label="Win Rate" value={pct(performance.win_rate_pct, { showSign: false })} sub="on closed trades" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile testid="kpi-rr" label="Risk / Reward" value={num(performance.risk_reward, 2)} />
        <Tile testid="kpi-pf" label="Profit Factor" value={num(performance.profit_factor, 2)} />
        <Tile testid="kpi-hold" label="Avg Holding (days)" value={num(performance.avg_holding_days, 0)} />
        <Tile testid="kpi-stocks" label="Active Stocks" value={`${kpis.stock_count ?? active_holdings.length}`} sub={`Cash: ${inr(kpis.cash_balance || 0)}`} />
      </div>
    </>
  );
}

// -----------------------------------------------------------------------------
// Performance chart — indexed to 100 for Portfolio, Nifty50, Smallcap50
// -----------------------------------------------------------------------------
function PerformanceChart({ series, kpis }) {
  const indexed = useMemo(() => {
    if (!series?.length) return [];
    // start values: first non-null per series
    const firstPort = series.find((s) => s.portfolio != null)?.portfolio;
    const firstN50 = series.find((s) => s.nifty50 != null)?.nifty50;
    const firstSc = series.find((s) => s.smallcap50 != null)?.smallcap50;
    return series.map((s) => ({
      date: s.date,
      portfolio: firstPort && s.portfolio != null ? (s.portfolio / firstPort) * 100 : null,
      nifty50: firstN50 && s.nifty50 != null ? (s.nifty50 / firstN50) * 100 : null,
      smallcap50: firstSc && s.smallcap50 != null ? (s.smallcap50 / firstSc) * 100 : null,
    }));
  }, [series]);

  if (!indexed.length) return null;

  // Legend badges use the SAME KPI values shown in the tiles above so customers
  // immediately see identical numbers in both places (no more chart-vs-KPI drift).
  const pRet = kpis?.absolute_returns_pct;
  const nRet = kpis?.nifty50_abs_pct;
  const sRet = kpis?.smallcap50_abs_pct;
  const badge = (v) => {
    if (v == null) return "";
    const cls = v > 0 ? "text-emerald-600" : v < 0 ? "text-rose-600" : "text-zinc-500";
    return <span className={`ml-1 font-mono ${cls}`}>({v > 0 ? "+" : ""}{v.toFixed(2)}%)</span>;
  };

  return (
    <Card className="border-zinc-200 shadow-none" data-testid="performance-chart">
      <CardContent className="p-0">
        <div className={SECTION_HEADER}>
          <div>
            <div className={SECTION_TITLE}>Portfolio Performance vs Benchmarks</div>
            <div className="text-xs text-zinc-500 mt-0.5">Indexed to 100 at portfolio start · higher = better relative performance</div>
          </div>
          <div className="hidden sm:flex items-center gap-4 text-xs">
            <span className="inline-flex items-center gap-1.5" data-testid="perf-legend-portfolio">
              <span className="w-3 h-0.5 bg-[#0b1a3a]" />Portfolio{badge(pRet)}
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="perf-legend-nifty50">
              <span className="w-3 h-0.5 border-t border-dashed border-[#2a78d6]" style={{ borderTopWidth: 2 }} />Nifty 50{badge(nRet)}
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="perf-legend-smallcap">
              <span className="w-3 h-0.5 border-t border-dashed border-[#f59e0b]" style={{ borderTopWidth: 2 }} />Nifty Smallcap 50{badge(sRet)}
            </span>
          </div>
        </div>
        <div className="p-4">
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <LineChart data={indexed} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#71717a" }} minTickGap={40} />
                <YAxis
                  tick={{ fontSize: 11, fill: "#71717a" }}
                  tickFormatter={(v) => `${(v - 100 >= 0 ? "+" : "")}${(v - 100).toFixed(0)}%`}
                  domain={["dataMin - 2", "dataMax + 2"]}
                />
                <RTooltip
                  formatter={(v, name) => {
                    if (v == null) return ["—", name];
                    return [`${v.toFixed(2)} (${(v - 100 >= 0 ? "+" : "")}${(v - 100).toFixed(2)}%)`, name];
                  }}
                  labelFormatter={(l) => fmtDate(l)}
                  contentStyle={{ fontSize: 12 }}
                />
                <Line type="monotone" dataKey="portfolio" name="Portfolio" stroke="#0b1a3a" strokeWidth={2.5} dot={false} connectNulls />
                <Line type="monotone" dataKey="nifty50" name="Nifty 50" stroke="#2a78d6" strokeWidth={2} strokeDasharray="6 4" dot={false} connectNulls />
                <Line type="monotone" dataKey="smallcap50" name="Nifty Smallcap 50" stroke="#f59e0b" strokeWidth={2} strokeDasharray="6 4" dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-2">{DISCLAIMER}</div>
        </div>
      </CardContent>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Industry Pie Chart (with Cash)
// -----------------------------------------------------------------------------
function IndustryPieChart({ industry_distribution, cash, total }) {
  const data = useMemo(() => {
    const rows = [...(industry_distribution || [])].map((r) => ({ industry: r.industry || "Other", value: r.value }));
    if (cash > 0) rows.unshift({ industry: "Cash", value: cash });
    return rows.filter((r) => r.value > 0);
  }, [industry_distribution, cash]);

  if (!data.length) return null;
  const grand = total || data.reduce((a, b) => a + b.value, 0);

  return (
    <Card className="border-zinc-200 shadow-none" data-testid="industry-pie-chart">
      <CardContent className="p-0">
        <div className={SECTION_HEADER}>
          <div>
            <div className={SECTION_TITLE}>Industry Distribution</div>
            <div className="text-xs text-zinc-500 mt-0.5">Cash shown as % of total portfolio value · higher cash = defensive positioning</div>
          </div>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="industry" cx="50%" cy="50%" innerRadius={60} outerRadius={110} paddingAngle={2}>
                  {data.map((entry, i) => (
                    <Cell key={entry.industry} fill={colorFor(entry.industry, i)} />
                  ))}
                </Pie>
                <RTooltip formatter={(v, n) => [`${inr(v)} (${((v / grand) * 100).toFixed(1)}%)`, n]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="overflow-y-auto max-h-[300px]">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                <tr><th className="text-left pb-2">Sector</th><th className="text-right pb-2">%</th><th className="text-right pb-2">Value</th></tr>
              </thead>
              <tbody>
                {data.map((r, i) => (
                  <tr key={r.industry} className="border-t border-zinc-100">
                    <td className="py-1.5 flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: colorFor(r.industry, i) }} />
                      <span className="text-zinc-800">{r.industry}</span>
                    </td>
                    <td className="text-right font-mono">{((r.value / grand) * 100).toFixed(1)}%</td>
                    <td className="text-right font-mono">{inr(r.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Cash % vs Active Stocks — dual Y axis
// -----------------------------------------------------------------------------
function CashVsStocksChart({ series, maxStocks = 50 }) {
  const chart = useMemo(() => (series || []).map((s) => ({ date: s.date, cash_pct: s.cash_pct ?? null, stocks: s.stocks ?? null })), [series]);
  if (!chart.length) return null;
  // Right-axis max is the strategy's exact stock capacity (25 / 50 / 100 / 500 / 15).
  const rightMax = maxStocks;
  return (
    <Card className="border-zinc-200 shadow-none" data-testid="cash-vs-stocks-chart">
      <CardContent className="p-0">
        <div className={SECTION_HEADER}>
          <div>
            <div className={SECTION_TITLE}>Cash Level &amp; Portfolio Concentration</div>
            <div className="text-xs text-zinc-500 mt-0.5">Higher cash levels indicate defensive positioning during uncertain markets</div>
          </div>
          <div className="hidden sm:flex items-center gap-4 text-xs">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#2a78d6]" style={{ height: 2 }} />Cash %</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 border-t border-dashed border-[#ea580c]" style={{ borderTopWidth: 2 }} />Active Stocks</span>
          </div>
        </div>
        <div className="p-4">
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={chart} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#71717a" }} minTickGap={40} />
                <YAxis yAxisId="cash" domain={[0, 100]} tick={{ fontSize: 11, fill: "#71717a" }} tickFormatter={(v) => `${v}%`} />
                <YAxis yAxisId="stocks" orientation="right" domain={[0, rightMax]} tick={{ fontSize: 11, fill: "#71717a" }} label={{ value: `Max ${maxStocks}`, angle: -90, position: 'insideRight', fill: '#a1a1aa', fontSize: 10 }} />
                <RTooltip labelFormatter={(l) => fmtDate(l)} contentStyle={{ fontSize: 12 }} formatter={(v, n) => n === "Cash %" ? [`${v?.toFixed?.(1) ?? v}%`, n] : [v, n]} />
                <Line yAxisId="cash" type="monotone" dataKey="cash_pct" name="Cash %" stroke="#2a78d6" strokeWidth={2.5} dot={false} connectNulls />
                <Line yAxisId="stocks" type="monotone" dataKey="stocks" name="Active Stocks" stroke="#ea580c" strokeWidth={2} strokeDasharray="6 4" dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Monthly Returns Table (Portfolio / Nifty 50 / Smallcap 50)
// -----------------------------------------------------------------------------
function MonthlyRow({ label, data, year, currentYm }) {
  return (
    <tr>
      <td className="py-2 pl-3 pr-2 text-xs font-semibold text-zinc-700 whitespace-nowrap sticky left-0 bg-white z-10">{label}</td>
      {MONTHS.map((m, i) => {
        const v = data[year]?.[i];
        const isCurrent = year === currentYm.y && i === currentYm.m;
        return (
          <td key={m} className={`py-2 px-2 text-right font-mono text-xs ${cellClass(v)}`}>
            {monthDisplay(v, isCurrent && v != null)}
          </td>
        );
      })}
    </tr>
  );
}

function MonthlyReturnsTable({ series }) {
  const { years, portfolio, nifty, smallcap } = useMemo(() => {
    const p = computeMonthlyReturns(series, "portfolio");
    const n = computeMonthlyReturns(series, "nifty50");
    const s = computeMonthlyReturns(series, "smallcap50");
    const yrs = Array.from(new Set([...Object.keys(p), ...Object.keys(n), ...Object.keys(s)])).sort();
    return { years: yrs, portfolio: p, nifty: n, smallcap: s };
  }, [series]);

  if (!years.length) return null;
  const now = new Date();
  const currentYm = { y: String(now.getFullYear()), m: now.getMonth() };

  return (
    <Card className="border-zinc-200 shadow-none" data-testid="monthly-returns-table">
      <CardContent className="p-0">
        <div className={SECTION_HEADER}>
          <div>
            <div className={SECTION_TITLE}>Monthly Returns</div>
            <div className="text-xs text-zinc-500 mt-0.5">Monthly performance comparison — Portfolio vs Benchmarks</div>
          </div>
          <div className="hidden sm:flex items-center gap-3 text-[11px]">
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-emerald-100 border border-emerald-300 rounded-sm" />&gt;3% gain</span>
            <span className="inline-flex items-center gap-1 text-emerald-700">■ Gain</span>
            <span className="inline-flex items-center gap-1 text-zinc-400">□ Flat</span>
            <span className="inline-flex items-center gap-1 text-rose-600">■ Loss</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-rose-100 border border-rose-300 rounded-sm" />&gt;3% loss</span>
          </div>
        </div>
        <div className="p-4 space-y-6">
          {years.map((y) => (
            <div key={y}>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2">Year {y}</div>
              <div className="overflow-x-auto">
                <table className="min-w-full border-separate border-spacing-0">
                  <thead>
                    <tr>
                      <th className="py-2 pl-3 pr-2 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-500 sticky left-0 bg-white z-10"></th>
                      {MONTHS.map((m) => (
                        <th key={m} className="py-2 px-2 text-right text-[10px] uppercase tracking-[0.15em] text-zinc-500">{m}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <MonthlyRow label="Portfolio" data={portfolio} year={y} currentYm={currentYm} />
                    <MonthlyRow label="Nifty 50" data={nifty} year={y} currentYm={currentYm} />
                    <MonthlyRow label="Smallcap 50" data={smallcap} year={y} currentYm={currentYm} />
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          <div className="text-[10px] text-zinc-400">* Month in progress</div>
        </div>
      </CardContent>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Active Holdings Table (with Day Change column)
// -----------------------------------------------------------------------------
// Returns { ltp, source, asOf } where source ∈ 'live' | 'closed' | 'bhavcopy' | 'unavailable'.
// The backend now tags each holding with `current_price_source` when it writes a real
// market price (either from live Yahoo merge or from a bhavcopy import). A missing/
// 'buy' source means the price is just the buy-time placeholder — we surface that as
// unavailable rather than pretending it's an LTP.
function resolveLtp(q, h) {
  if (q?.price != null) {
    const isLive = q.market_state === "REGULAR";
    return { ltp: q.price, source: isLive ? "live" : "closed", asOf: null };
  }
  const src = h.current_price_source;
  const asOf = h.current_price_asof || null;
  if (src === "live" && h.current_price != null) return { ltp: h.current_price, source: "live", asOf };
  if (src === "bhavcopy" && h.current_price != null) return { ltp: h.current_price, source: "bhavcopy", asOf };
  if (h.live_ltp != null) return { ltp: h.live_ltp, source: "closed", asOf: null };
  // Fallback for legacy rows that pre-date current_price_source tagging.
  if (h.current_price != null && h.current_price !== h.buy_price) {
    return { ltp: h.current_price, source: "bhavcopy", asOf };
  }
  return { ltp: null, source: "unavailable", asOf: null };
}

function DayChangeCell({ q, h, marketState }) {
  const change = q?.change ?? h.live_change;
  const changePct = q?.change_pct ?? h.live_change_pct;
  if (change == null || changePct == null) return <span className="text-zinc-400 font-mono">—</span>;
  const color = change > 0 ? "text-profit" : change < 0 ? "text-loss" : "text-zinc-500";
  const arrow = change > 0 ? "▲" : change < 0 ? "▼" : "■";
  const isClosed = (q?.market_state && q.market_state !== "REGULAR") || (marketState && marketState !== "REGULAR");
  return (
    <span className={`inline-flex flex-col items-end font-mono text-xs ${color}`}>
      <span>{arrow} {inr(Math.abs(change), { decimals: 2 })} ({changePct >= 0 ? "+" : ""}{changePct.toFixed(2)}%)</span>
      {isClosed && <span className="text-[9px] text-zinc-400 mt-0.5">prev close</span>}
    </span>
  );
}

function LtpCell({ resolved }) {
  const { ltp, source, asOf } = resolved;
  if (ltp == null) {
    return (
      <span className="inline-flex flex-col items-end">
        <span className="text-zinc-400 font-mono">—</span>
        <span className="text-[9px] uppercase tracking-wider text-zinc-400">Live N/A</span>
      </span>
    );
  }
  const label =
    source === "live" ? "LIVE" :
    source === "closed" ? "Last traded" :
    source === "bhavcopy" ? (asOf ? `as of ${fmtDate(asOf)}` : "Prev close") :
    "cached";
  const dot = source === "live" ? <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" title="Live" /> : null;
  return (
    <span className="inline-flex flex-col items-end">
      <span className="inline-flex items-center gap-1.5">
        {dot}
        <span className="font-mono">{inr(ltp, { decimals: 2 })}</span>
      </span>
      <span className="text-[9px] uppercase tracking-wider text-zinc-400">{label}</span>
    </span>
  );
}

function ActiveHoldingsTable({ active_holdings, ltpFor, marketState, totals }) {
  return (
    <Card className="border-zinc-200 shadow-none">
      <CardContent className="p-0">
        <div className={SECTION_HEADER}>
          <div className={SECTION_TITLE}>Active Holdings</div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 hidden sm:inline">Prices delayed ~15 min · Yahoo Finance</span>
            <Badge className="bg-green-50 text-green-700 border-green-200">{active_holdings.length}</Badge>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right w-12">No.</TableHead>
                <TableHead className="sticky left-0 bg-white z-10">Symbol</TableHead>
                <TableHead>Industry</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Buy Date</TableHead>
                <TableHead className="text-center">Holding Days</TableHead>
                <TableHead className="text-right">Buy Rate</TableHead>
                <TableHead className="text-right">Buy Amt</TableHead>
                <TableHead className="text-right">LTP</TableHead>
                <TableHead className="text-right">Day Chg</TableHead>
                <TableHead className="text-right">Current Value</TableHead>
                <TableHead className="text-right">P&amp;L</TableHead>
                <TableHead className="text-right">% P&amp;L</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {active_holdings.length === 0 && <TableRow><TableCell colSpan={13} className="text-zinc-500">No holdings yet. Engine will buy on next run.</TableCell></TableRow>}
              {[...active_holdings]
                .map((h) => ({ ...h, _hd: holdingDays(h.buy_date) }))
                .sort((a, b) => b._hd - a._hd)
                .map((h, idx) => {
                  const q = ltpFor(h.symbol);
                  const resolved = resolveLtp(q, h);
                  const buyAmt = h.buy_price * h.qty;
                  const hasLtp = resolved.ltp != null;
                  const curValue = hasLtp ? resolved.ltp * h.qty : null;
                  const pnl = hasLtp ? curValue - buyAmt : null;
                  const pnlPct = hasLtp && buyAmt ? (pnl / buyAmt) * 100 : null;
                  const hd = h._hd;
                  return (
                    <TableRow key={h.holding_id} data-testid={`holding-${h.symbol}`}>
                      <TableCell className="text-right font-mono text-zinc-500 text-xs">{idx + 1}</TableCell>
                      <TableCell className="font-mono font-semibold sticky left-0 bg-white z-10">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help border-b border-dotted border-zinc-300">{h.symbol}</span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="font-sans">{h.company}</TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell className="text-sm text-zinc-600">{h.industry}</TableCell>
                      <TableCell className="text-right font-mono">{h.qty}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{fmtDate(h.buy_date)}</TableCell>
                      <TableCell className="text-center font-mono text-sm">
                        {hd >= 30
                          ? <Badge className="bg-zinc-100 text-zinc-700 border-zinc-200 font-mono font-normal">{hd} days</Badge>
                          : <span className="text-zinc-700">{hd} days</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono" data-testid={`buy-rate-${h.symbol}`}>{inr(h.buy_price, { decimals: 2 })}</TableCell>
                      <TableCell className="text-right font-mono">{inr(buyAmt)}</TableCell>
                      <TableCell className="text-right" data-testid={`ltp-${h.symbol}`}>
                        <LtpCell resolved={resolved} />
                      </TableCell>
                      <TableCell className="text-right" data-testid={`day-chg-${h.symbol}`}>
                        <DayChangeCell q={q} h={h} marketState={marketState} />
                      </TableCell>
                      <TableCell className="text-right font-mono">{hasLtp ? inr(curValue) : <span className="text-zinc-400">—</span>}</TableCell>
                      <TableCell className={`text-right font-mono ${hasLtp ? profitColor(pnl) : "text-zinc-400"}`}>{hasLtp ? inr(pnl, { showSign: true }) : "—"}</TableCell>
                      <TableCell className={`text-right font-mono ${hasLtp ? profitColor(pnlPct) : "text-zinc-400"}`}>{hasLtp ? pct(pnlPct) : "—"}</TableCell>
                    </TableRow>
                  );
                })}
              {active_holdings.length > 0 && (
                <TableRow data-testid="active-totals-row" className="bg-zinc-50 border-t-2 border-zinc-300 font-semibold">
                  <TableCell colSpan={7} className="text-right text-xs uppercase tracking-wider text-zinc-600">Total</TableCell>
                  <TableCell className="text-right font-mono">{inr(totals.totBuy)}</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell className="text-right font-mono">{totals.hasAnyLtp ? inr(totals.totCur) : <span className="text-zinc-400">—</span>}</TableCell>
                  <TableCell className={`text-right font-mono ${totals.hasAnyLtp ? profitColor(totals.totPnl) : "text-zinc-400"}`}>{totals.hasAnyLtp ? inr(totals.totPnl, { showSign: true }) : "—"}</TableCell>
                  <TableCell className={`text-right font-mono ${totals.hasAnyLtp ? profitColor(totals.totPnlPct) : "text-zinc-400"}`}>{totals.hasAnyLtp ? pct(totals.totPnlPct) : "—"}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="px-6 py-2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 border-t border-zinc-100">{DISCLAIMER}</div>
      </CardContent>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Exited Holdings (unchanged)
// -----------------------------------------------------------------------------
function ExitedHoldingsTable({ exited_holdings }) {
  const totals = useMemo(() => {
    let totBuy = 0, totSell = 0, totPnl = 0;
    exited_holdings.forEach((h) => {
      const bA = (h.buy_price || 0) * (h.qty || 0);
      const sA = (h.sell_price || 0) * (h.qty || 0);
      totBuy += bA;
      totSell += sA;
      totPnl += h.pnl != null ? h.pnl : (sA - bA);
    });
    return { totBuy, totSell, totPnl, totPnlPct: totBuy ? (totPnl / totBuy) * 100 : 0 };
  }, [exited_holdings]);
  return (
    <Card className="border-zinc-200 shadow-none">
      <CardContent className="p-0">
        <div className={SECTION_HEADER}>
          <div className={SECTION_TITLE}>Exited Stocks</div>
          <Badge className="bg-zinc-50 text-zinc-700 border-zinc-200">{exited_holdings.length}</Badge>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right w-12">No.</TableHead>
                <TableHead>Symbol</TableHead>
                <TableHead>Industry</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Buy Date</TableHead>
                <TableHead className="text-right">Buy Rate</TableHead>
                <TableHead className="text-right">Buy Amt</TableHead>
                <TableHead className="text-right">Sell Date</TableHead>
                <TableHead className="text-right">Sell Rate</TableHead>
                <TableHead className="text-right">Sell Amt</TableHead>
                <TableHead className="text-right">P&amp;L</TableHead>
                <TableHead className="text-right">% P&amp;L</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {exited_holdings.length === 0 && <TableRow><TableCell colSpan={12} className="text-zinc-500">No exits yet.</TableCell></TableRow>}
              {exited_holdings.map((h, idx) => {
                const buyAmt = h.buy_price * h.qty;
                const sellAmt = (h.sell_price || 0) * h.qty;
                const pnl = h.pnl ?? (sellAmt - buyAmt);
                const pnlPct = buyAmt ? (pnl / buyAmt) * 100 : 0;
                return (
                  <TableRow key={h.holding_id} data-testid={`exited-${h.symbol}`}>
                    <TableCell className="text-right font-mono text-zinc-500 text-xs">{idx + 1}</TableCell>
                    <TableCell className="font-mono font-semibold">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help border-b border-dotted border-zinc-300">{h.symbol}</span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="font-sans">{h.company}</TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell className="text-sm text-zinc-600">{h.industry}</TableCell>
                    <TableCell className="text-right font-mono">{h.qty}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{fmtDate(h.buy_date)}</TableCell>
                    <TableCell className="text-right font-mono">{inr(h.buy_price, { decimals: 2 })}</TableCell>
                    <TableCell className="text-right font-mono">{inr(buyAmt)}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{fmtDate(h.sell_date)}</TableCell>
                    <TableCell className="text-right font-mono">{inr(h.sell_price, { decimals: 2 })}</TableCell>
                    <TableCell className="text-right font-mono">{inr(sellAmt)}</TableCell>
                    <TableCell className={`text-right font-mono ${profitColor(pnl)}`}>{inr(pnl, { showSign: true })}</TableCell>
                    <TableCell className={`text-right font-mono ${profitColor(pnlPct)}`}>{pct(pnlPct)}</TableCell>
                  </TableRow>
                );
              })}
              {exited_holdings.length > 0 && (
                <TableRow data-testid="exited-totals-row" className="bg-zinc-50 border-t-2 border-zinc-300 font-semibold">
                  <TableCell colSpan={6} className="text-right text-xs uppercase tracking-wider text-zinc-600">Total</TableCell>
                  <TableCell className="text-right font-mono">{inr(totals.totBuy)}</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell className="text-right font-mono">{inr(totals.totSell)}</TableCell>
                  <TableCell className={`text-right font-mono ${profitColor(totals.totPnl)}`}>{inr(totals.totPnl, { showSign: true })}</TableCell>
                  <TableCell className={`text-right font-mono ${profitColor(totals.totPnlPct)}`}>{pct(totals.totPnlPct)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
